"use client";

import * as React from "react";
import { ArrowRight, TrendingUp, Users, Zap } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const successStories = [{
  id: 1,
  institution: "Mid-Sized Liberal Arts College",
  metric: "100% Internal Launch Capacity",
  description: "Transitioned from full OPM dependency to a fully autonomous internal program management team within 18 months.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/university_arch_detail_1.png",
  icon: Zap,
  statLabel: "Capability Gained"
}, {
  id: 2,
  institution: "State University System",
  metric: "35% Increase in Faculty Engagement",
  description: "Co-designed a lifelong learning curriculum framework that aligned faculty incentives with learner outcomes.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/university_arch_detail_2.png",
  icon: Users,
  statLabel: "Cultural Shift"
}, {
  id: 3,
  institution: "Private Research Institute",
  metric: "3x Faster Program Development",
  description: "Established agile cross-functional teams that reduced new certificate launch times from 14 months to 4 months.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/university_arch_detail_3.png",
  icon: TrendingUp,
  statLabel: "Operational Efficiency"
}];
export default function RealWorldImpact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="real-world-impact" ref={ref} className={cn("bg-background relative w-full py-24 overflow-hidden transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h2 className="text-primary font-serif text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            Real-World Impact
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl leading-relaxed">
            We don't just deliver reports; we build the internal muscle for
            sustained growth. Here is how our partners are thriving independently.
          </p>
        </div>

        <div className="relative">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {successStories.map((story, index) => <CarouselItem key={story.id} data-index={index} className="pl-4 md:basis-4/5 lg:basis-3/4 xl:basis-2/3">
                  <div className="group relative h-[500px] md:h-[600px] w-full overflow-hidden rounded-xl shadow-xl">
                    {}
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{
                  backgroundImage: `url(${story.image})`
                }} />
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary)]/90 via-[var(--primary)]/40 to-transparent" />

                    {}
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 lg:p-12">
                      <Card className="bg-card/95 backdrop-blur-sm border-none shadow-2xl max-w-xl overflow-hidden">
                        <div className="h-1 w-full bg-secondary" />
                        <CardContent className="p-6 md:p-8">
                          <div className="flex items-center gap-3 mb-4 text-secondary font-medium text-sm uppercase tracking-wider">
                            <story.icon className="size-5" />
                            <span>{story.statLabel}</span>
                          </div>
                          
                          <h3 className="text-primary font-serif text-2xl md:text-3xl font-bold mb-3 leading-tight">
                            {story.metric}
                          </h3>
                          
                          <p className="text-muted-foreground text-base md:text-lg mb-6 leading-relaxed">
                            {story.description}
                          </p>
                          
                          <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
                            <span className="text-sm font-semibold text-primary/80">
                              {story.institution}
                            </span>
                            <Link to="/ImpactStoriesPage">
                              <Button variant="ghost" size="sm" className="group/btn p-0 hover:bg-transparent hover:text-secondary">
                                Read Story
                                <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="hidden md:flex absolute -top-24 right-0 gap-2">
              <CarouselPrevious className="static translate-y-0 hover:bg-secondary hover:text-white border-primary/20" />
              <CarouselNext className="static translate-y-0 hover:bg-secondary hover:text-white border-primary/20" />
            </div>
          </Carousel>
        </div>

        <div className="mt-12 text-center md:hidden">
          <Link to="/ImpactStoriesPage">
            <Button variant="outline" className="w-full">
              View All Impact Stories
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}